import { useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Checkbox,
    FruitfulCustomIcon,
    H6Regular,
    XStack,
    YStack,
} from 'smoothie';

const MAX_BENEFICIARIES = 4;

export interface SelectAccountOption {
    id: string;
    nickname: string;
    isJointAccount: boolean;
    icon: string;
    color: string;
    beneficiaryCount: number;
}

interface SelectBeneficiaryAccountsProps {
    accounts: SelectAccountOption[];
    onContinue: (selectedAccountIds: string[]) => void;
}

export const SelectBeneficiaryAccounts = ({
    accounts,
    onContinue,
}: SelectBeneficiaryAccountsProps) => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(
        () => new Set(),
    );

    const selectableAccounts = accounts.filter(
        (a) => a.beneficiaryCount < MAX_BENEFICIARIES,
    );
    const isAllSelected =
        selectableAccounts.length > 0 &&
        selectableAccounts.every((a) => selectedIds.has(a.id));

    const toggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(selectableAccounts.map((a) => a.id)));
        }
    };

    const toggleAccount = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    return (
        <YStack gap={24} paddingVertical={24}>
            <H6Regular>
                Which accounts would you like to add this beneficiary to?
            </H6Regular>
            <YStack borderRadius={24} overflow="hidden">
                <XStack
                    gap={12}
                    padding={16}
                    alignItems="center"
                    backgroundColor="$gray100"
                    onPress={toggleSelectAll}
                    pressStyle={{ opacity: 0.7 }}
                >
                    <YStack flex={1}>
                        <BodyMediumRegular>Select all</BodyMediumRegular>
                    </YStack>
                    <Checkbox
                        id="select-all"
                        checked={isAllSelected}
                        onPress={() => {
                            toggleSelectAll();
                        }}
                    />
                </XStack>
                {accounts.map((account) => {
                    const isDisabled =
                        account.beneficiaryCount >= MAX_BENEFICIARIES;
                    return (
                        <XStack
                            key={account.id}
                            gap={12}
                            padding={16}
                            alignItems="center"
                            backgroundColor="$gray100"
                            opacity={isDisabled ? 0.5 : 1}
                            onPress={
                                isDisabled
                                    ? undefined
                                    : () => {
                                          toggleAccount(account.id);
                                      }
                            }
                            pressStyle={
                                isDisabled ? undefined : { opacity: 0.7 }
                            }
                        >
                            <FruitfulCustomIcon
                                icon={account.icon}
                                color={account.color}
                                size="xs"
                            />
                            <YStack flex={1} gap={4}>
                                <BodyMediumRegular>
                                    {account.nickname}
                                </BodyMediumRegular>
                                <BodySmallRegular color="$gray1000">
                                    {isDisabled
                                        ? `${MAX_BENEFICIARIES}/${MAX_BENEFICIARIES} beneficiaries`
                                        : account.isJointAccount
                                          ? 'Joint account'
                                          : 'Individual account'}
                                </BodySmallRegular>
                            </YStack>
                            <Checkbox
                                id={`account-${account.id}`}
                                checked={selectedIds.has(account.id)}
                                disabled={isDisabled}
                                onPress={(e) => {
                                    e.stopPropagation();
                                    if (!isDisabled) toggleAccount(account.id);
                                }}
                            />
                        </XStack>
                    );
                })}
            </YStack>
            <Button
                text="Continue"
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                disabled={selectedIds.size === 0}
                onPress={() => {
                    onContinue(Array.from(selectedIds));
                }}
            />
        </YStack>
    );
};
